import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import type { AdminCheckoutLink } from '@etiquetachef/types';
import {
  adminGetCheckoutLinksCallable,
  adminSendCheckoutLinkEmailCallable,
} from '@/lib/callables';
import { formatDateTime, formatPriceCents } from '@/lib/format';
import { toast } from '@/lib/notifications';

type Props = {
  /** `string` e não `TenantId`: a rota entrega o id cru do path, e o schema
      Zod do callable é quem faz o branding. Mesmo contrato do `BillingCard`. */
  tenantId: string;
};

/**
 * Links de pagamento do tenant no console de suporte: lista, copia o link vivo
 * e reenvia o e-mail ao cliente.
 *
 * Origem: #289 — 2026-08-29 14:00 +0200 (super-admin gera, vê e reenvia o
 * link de pagamento).
 *
 * Por quê: a lista vem de um callable (`adminGetCheckoutLinks`), não de um
 * listener do Firestore, porque metade da informação que importa está no
 * gateway: se a sessão ainda está aberta e se a URL de recuperação já venceu.
 * O servidor cruza o nosso `checkoutAttempts` com o Stripe e devolve
 * `is_live_link`/`live_url` já resolvidos — a tela não adivinha. Consequência
 * aceita: sem realtime; "Atualizar" refaz a consulta.
 *
 * Contrato: carrega ao montar e a cada `tenantId`. `stale: true` (gateway fora
 * do ar; o servidor não lança) vira aviso. "Enviar e-mail" só com
 * `is_live_link` (`adminSendCheckoutLinkEmail`, que decide sozinho se a
 * sessão é do tenant e ainda vale). Erro vira `Alert`; mensagem vazia cai no
 * genérico. Permissão no servidor: `adminGetCheckoutLinks` aceita staff de
 * qualquer papel admin (leitura); `adminSendCheckoutLinkEmail` exige
 * `ADMIN_PERMISSIONS.manageSubscriptions` (ROOT/FINANCE). A consulta de
 * `/checkoutAttempts` (índice `tenant_id` + `created_at`) é do servidor.
 *
 * Usado por: `routes/TenantDetailPage.tsx` (rota `/tenants/:id`, aba Cobrança,
 * menu Tenants; spec `docs/screens/tenant-detail-admin.md`), admin web;
 * `onCheckoutLinkCreated` do `BillingActions` chama o `load` do pai.
 *
 * Dados: `AdminCheckoutLink` (`AdminCheckoutLinkSchema`,
 * `packages/types/src/schemas.ts`): `amount_cents` em CENTAVOS
 * (`formatPriceCents`), `created_at`/`expires_at`/`recovery_expires_at` epoch
 * ms ou `null`, `provider` ('stripe' | 'asaas', #776), `source: 'GATEWAY'` =
 * sessão que só o Stripe conhece, `last_failure_code` = código cru do gateway
 * (chave i18n desconhecida cai no código).
 *
 * Falhas conhecidas:
 *  - 2026-08-29 (#289): `t` nas dependências do `useCallback` recriava `load`
 *    a cada render e a tela ficava presa em "carregando" refazendo a consulta.
 *
 * Histórico:
 *  - 2026-08-29 17:41 +0200 — (#289) badge de sessão vinda do gateway.
 *  - 2026-09-29 12:36 +0200 — (#776) coluna PSP.
 *  - 2026-10-10 14:40 +0200 — (#798) rota e permissões (#796).
 */
export function CheckoutLinksCard({ tenantId }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [links, setLinks] = useState<readonly AdminCheckoutLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);

  // Guarda a mensagem CRUA e traduz no render. `t` fora das dependências de
  // propósito: o `t` do i18next não é garantidamente estável entre renders, e
  // tê-lo aqui recriava `load` a cada render — o efeito abaixo disparava de
  // novo, e a tela ficava presa em "carregando" refazendo a consulta sem fim.
  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminGetCheckoutLinksCallable({ tenantId });
      setLinks(result.links);
      setStale(result.stale);
    } catch (err) {
      setError(err instanceof Error ? err.message : '');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Reenvia ao cliente o e-mail com o link vivo (`adminSendCheckoutLinkEmail`).
   *
   * Origem: #289 — 2026-08-29 14:00 +0200.
   *
   * Contrato: input `{ "tenantId": "tenant-1", "sessionId": "cs_test_000" }`;
   * saída `{ email }` (destinatário, só para o toast). O servidor decide se a
   * sessão é do tenant e ainda vale. Erro → toast com `err.message`. Envia
   * e-mail real a cada clique: o botão fica em `loading` pela `sendingId`.
   *
   * Usado por: botão "Enviar e-mail" da linha (só com `is_live_link`).
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   */
  async function handleSend(link: AdminCheckoutLink): Promise<void> {
    setSendingId(link.session_id);
    try {
      const result = await adminSendCheckoutLinkEmailCallable({
        tenantId,
        sessionId: link.session_id,
      });
      toast.success(
        t('billing.checkoutLink.sendSuccess', { email: result.email }),
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'),
      );
    } finally {
      setSendingId(null);
    }
  }

  const locale = i18n.language;

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={t('billing.checkoutLink.cardDescription')}
          actions={
            <Button iconName="refresh" loading={loading} onClick={(): void => void load()}>
              {t('common.refresh')}
            </Button>
          }
        >
          {t('billing.checkoutLink.cardTitle')}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {error !== null ? (
          <Alert type="error">
            {error === '' ? t('tenantDetail.actions.errorGeneric') : error}
          </Alert>
        ) : null}
        {stale ? (
          <Alert type="warning">{t('billing.checkoutLink.staleWarning')}</Alert>
        ) : null}
        <Table
          variant="embedded"
          loading={loading}
          loadingText={t('common.loading')}
          items={[...links]}
          trackBy="session_id"
          empty={
            <Box textAlign="center" color="inherit" padding={{ vertical: 'm' }}>
              {t('billing.checkoutLink.empty')}
            </Box>
          }
          columnDefinitions={[
            {
              id: 'created',
              header: t('billing.checkoutLink.columns.created'),
              cell: (item): string => formatDateTime(item.created_at, locale),
            },
            {
              id: 'status',
              header: t('billing.checkoutLink.columns.status'),
              cell: (item): React.JSX.Element => (
                <SpaceBetween size="xxs" direction="vertical">
                  <SpaceBetween size="xs" direction="horizontal">
                  <Badge color={badgeColorFor(item.status)}>
                    {t(`billing.checkoutLink.status.${item.status}`)}
                  </Badge>
                  {/* Sessão que a Stripe conhece e nós não: veio de fora do
                      nosso fluxo (Payment Link montado à mão no Dashboard). */}
                  {item.source === 'GATEWAY' ? (
                    <Badge color="grey">{t('billing.checkoutLink.fromGateway')}</Badge>
                  ) : null}
                  {!item.livemode ? (
                    <Badge color="grey">{t('billing.checkoutLink.testMode')}</Badge>
                  ) : null}
                  </SpaceBetween>
                  {/* O motivo é o que diz ao vendedor o que falar ao ligar:
                      "o QR do Pix expirou" pede outra abordagem que "o cartão
                      foi recusado". Chave desconhecida cai no código cru — a
                      Stripe pode introduzir códigos novos, e mostrar o código
                      é melhor que esconder a informação. */}
                  {item.last_failure_code ? (
                    <Box variant="small" color="text-status-error">
                      {t(`billing.checkoutLink.failure.${item.last_failure_code}`, {
                        defaultValue: item.last_failure_code,
                      })}
                    </Box>
                  ) : null}
                </SpaceBetween>
              ),
            },
            {
              id: 'amount',
              header: t('billing.checkoutLink.columns.amount'),
              cell: (item): string =>
                item.amount_cents != null && item.currency
                  ? formatPriceCents(item.amount_cents, item.currency, locale)
                  : '—',
            },
            // #776: de qual PSP é o link (Stripe ou Asaas) — o mesmo tenant
            // pode ter links dos dois quando migrou de gateway.
            {
              id: 'psp',
              header: t('billing.checkoutLink.columns.psp'),
              cell: (item): string => item.provider,
            },
            {
              id: 'methods',
              header: t('billing.checkoutLink.columns.methods'),
              cell: (item): string =>
                item.payment_method_types && item.payment_method_types.length > 0
                  ? item.payment_method_types.join(', ')
                  : '—',
            },
            {
              id: 'attempts',
              header: t('billing.checkoutLink.columns.attempts'),
              cell: (item): string =>
                item.attempt_count != null ? String(item.attempt_count) : '—',
            },
            {
              id: 'validity',
              header: t('billing.checkoutLink.columns.validity'),
              cell: (item): string => describeValidity(item, locale, t),
            },
            {
              id: 'link',
              header: t('billing.checkoutLink.columns.link'),
              cell: (item): React.JSX.Element =>
                // Link morto não ganha botão de copiar: copiar uma URL que não
                // abre nada é pior que não ter botão — o operador só descobre
                // depois de já ter mandado para o cliente.
                item.is_live_link && item.live_url ? (
                  <CopyToClipboard
                    variant="inline"
                    textToCopy={item.live_url}
                    textToDisplay={
                      <Box variant="code">
                        {item.recovery_url === item.live_url
                          ? t('billing.checkoutLink.recoveryLink')
                          : t('billing.checkoutLink.sessionLink')}
                      </Box>
                    }
                    copyButtonAriaLabel={t('common.copy')}
                    copySuccessText={t('common.copied')}
                    copyErrorText={t('common.copyError')}
                  />
                ) : (
                  <Box color="text-status-inactive">
                    {t('billing.checkoutLink.noLiveLink')}
                  </Box>
                ),
            },
            {
              id: 'actions',
              header: t('billing.checkoutLink.columns.actions'),
              cell: (item): React.JSX.Element => (
                <Button
                  variant="normal"
                  disabled={!item.is_live_link}
                  loading={sendingId === item.session_id}
                  onClick={(): void => void handleSend(item)}
                >
                  {t('billing.checkoutLink.sendEmail')}
                </Button>
              ),
            },
          ]}
        />
      </SpaceBetween>
    </Container>
  );
}

/** Cor do badge por status da tentativa. `switch` exaustivo: status novo reprova o typecheck. */
function badgeColorFor(
  status: AdminCheckoutLink['status'],
): 'green' | 'red' | 'blue' | 'grey' {
  switch (status) {
    case 'COMPLETED':
      return 'green';
    case 'FAILED':
      return 'red';
    case 'OPEN':
      return 'blue';
    case 'EXPIRED':
      return 'grey';
  }
}

/**
 * Mostra a validade do que o operador pode usar AGORA. Com a sessão viva, o que
 * vale é o `expires_at` dela (máx. 24h — os 72h configurados são a janela do QR
 * do Pix dentro da sessão, não a vida do link). Depois, o prazo da recuperação.
 *
 * Origem: #289 — 2026-08-29 14:00 +0200.
 *
 * Usado por: coluna "Validade" da tabela deste card.
 */
function describeValidity(
  item: AdminCheckoutLink,
  locale: string,
  t: (key: string) => string,
): string {
  if (!item.is_live_link) return t('billing.checkoutLink.expiredValidity');
  if (item.live_url === item.recovery_url && item.recovery_expires_at != null) {
    return formatDateTime(item.recovery_expires_at, locale);
  }
  if (item.expires_at != null) return formatDateTime(item.expires_at, locale);
  return '—';
}
